import React, { useEffect, useCallback, useState } from 'react'
import useSWR from 'swr'
import { Field, FieldProps, Form, Formik } from 'formik'
import {
  Accordion,
  AlignItems,
  Box,
  Button,
  Color,
  FlexDirection,
  FontWeight,
  Link,
  Join,
  Space,
  Text,
  InputField,
  Radio,
  Select,
  Display,
  ButtonColorVariant,
  Icon,
  LinkVariant,
  Input,
} from '@gousto-internal/citrus-react'
import { fetchUserShippingAddresses } from '@library/api-user'

import { fieldsEditAddress, optionsNotGoingToBeIn } from './config'
import { RenderedTitle } from '../RenderedTitle'
import { ApiKeys } from '../../apiKeys'

import type { ShippingAddress } from '@library/api-user'

import './DeliveryAddressSection.css'

type RadioState = { id: string; checked: boolean | undefined; shouldEnterAddressManually: boolean }

function DeliveryAddressSection() {
  const { data: addresses } = useSWR(ApiKeys.UserCurrentShippingAddress, fetchUserShippingAddresses)
  const [radioGroup, setRadioGroup] = useState<RadioState[]>()

  useEffect(() => {
    if (Array.isArray(addresses)) {
      const radioStateValue = addresses.map((address) => ({
        id: address.id,
        checked: false,
        shouldEnterAddressManually: false,
      }))

      setRadioGroup(radioStateValue)
    }
  }, [addresses])

  const handleFoldedAccordion = useCallback((isExpanded: boolean) => {
    return <RenderedTitle isExpanded={isExpanded} title="Delivery address" iconType="home" />
  }, [])

  const handleRadioClick = useCallback(
    (event: React.SyntheticEvent) => {
      const target = event.currentTarget
      const newState =
        radioGroup &&
        radioGroup.map((radio: RadioState) => ({
          ...radio,
          checked: radio.id === target.id,
          shouldEnterAddressManually: false,
        }))

      setRadioGroup(newState)
    },
    [radioGroup],
  )

  const handleEnterAddress = useCallback(
    (event: React.SyntheticEvent) => {
      const target = event.currentTarget

      const newState =
        radioGroup &&
        radioGroup.map((radio: RadioState) => ({
          ...radio,
          shouldEnterAddressManually: radio.id === target.id,
        }))

      setRadioGroup(newState)
    },
    [radioGroup],
  )

  return (
    <>
      <Space size={4} direction="vertical" />
      <Accordion
        id={'delivery-info-accordion'}
        title={handleFoldedAccordion}
        initialState="expanded"
      >
        {addresses &&
          radioGroup &&
          addresses.map((shippingAddress: ShippingAddress, index: number) => {
            return (
              <div key={radioGroup[index].id}>
                <Join with={<Space size={1} direction="vertical" />}>
                  <Box paddingH={0} paddingV={1}>
                    <Radio
                      name={`radio-${shippingAddress.id}`}
                      id={shippingAddress.id}
                      outline
                      checked={radioGroup[index].checked}
                      onChange={handleRadioClick}
                    >
                      {(focused: boolean) => {
                        return (
                          <Box flexGrow={1} display="flex" flexDirection={FlexDirection.Row}>
                            <Space size={2} direction="horizontal" />
                            <Box display="flex" flexGrow={1} flexDirection={FlexDirection.Column}>
                              <Text
                                color={focused ? Color.Secondary_400 : Color.ColdGrey_800}
                                fontWeight={focused ? FontWeight.SemiBold : FontWeight.Normal}
                                size={2}
                              >
                                {shippingAddress.name}
                              </Text>
                              <Text size={1} color={Color.ColdGrey_600}>
                                {`${shippingAddress.line1}, ${shippingAddress.postcode}`}
                              </Text>
                            </Box>
                          </Box>
                        )
                      }}
                    </Radio>
                  </Box>
                  {radioGroup[index].checked && (
                    <Box paddingBottom={8}>
                      <Box paddingV={2} color={Color.Error_700} display={Display.Flex}>
                        <Link
                          variant={LinkVariant.None}
                          color={Color.Error_700}
                          disabled={shippingAddress.shippingDefault}
                        >
                          <Icon name="delete" color={Color.Inherit} size={5} />
                          <Text color={Color.Inherit} fontWeight={FontWeight.Bold} underline>
                            Delete this address
                          </Text>
                        </Link>
                      </Box>
                      <Formik
                        initialValues={shippingAddress}
                        onSubmit={() => {
                          console.log('submit')
                        }}
                      >
                        <Form>
                          <Box paddingBottom={2}>
                            <Field name="name">
                              {({ field }: FieldProps) => (
                                <InputField
                                  id={field.name}
                                  label="Address Name"
                                  placeholder="Name (e.g. Home, Work)"
                                  {...field}
                                />
                              )}
                            </Field>
                          </Box>
                          <Box
                            display="flex"
                            flexDirection={FlexDirection.Row}
                            alignItems={AlignItems.FlexEnd}
                            paddingBottom={2}
                          >
                            <Box paddingRight={1} flexGrow={2}>
                              <Field name="postcode">
                                {({ field }: FieldProps) => (
                                  <InputField
                                    id={field.name}
                                    label="Postcode"
                                    placeholder="Postcode"
                                    {...field}
                                  />
                                )}
                              </Field>
                            </Box>
                            <Box>
                              <Button type="button" height="3rem">
                                Search
                              </Button>
                            </Box>
                          </Box>
                          <Box paddingBottom={2}>
                            <Select id="delivery-address" disabled>
                              <option value={shippingAddress.line1}>
                                {`${shippingAddress.line1}, ${shippingAddress.postcode}`}
                              </option>
                            </Select>
                          </Box>
                          {!radioGroup[index].shouldEnterAddressManually && (
                            <Box paddingBottom={2}>
                              <Link id={radioGroup[index].id} size={2} onClick={handleEnterAddress}>
                                Enter address manually
                              </Link>
                            </Box>
                          )}
                          {radioGroup[index].shouldEnterAddressManually && (
                            <Box paddingBottom={2}>
                              {fieldsEditAddress.map((config) => (
                                <Field
                                  name={config.name}
                                  key={`${shippingAddress.id}-edit-${config.name}`}
                                >
                                  {({ field }: FieldProps) => (
                                    <Box paddingBottom={2}>
                                      <Input
                                        id={config.name}
                                        placeholder={config.placeholder}
                                        {...field}
                                      />
                                    </Box>
                                  )}
                                </Field>
                              ))}
                            </Box>
                          )}
                          <Box>
                            <Text size={2} fontWeight={FontWeight.SemiBold}>
                              {`Where can we leave your box if you're not in?`}
                            </Text>
                            <Select id="deliveryInstructions">
                              {optionsNotGoingToBeIn.map((optionValue) => {
                                return (
                                  <option
                                    value={optionValue}
                                    key={optionValue}
                                    selected={optionValue === shippingAddress.deliveryInstructions}
                                  >
                                    {optionValue}
                                  </option>
                                )
                              })}
                            </Select>
                          </Box>
                          <Box paddingBottom={8} paddingV={2}>
                            <Text size={1} color={Color.ColdGrey_600}>
                              To ensure your box is delivered safely, we cannot accept instructions
                              to leave boxes over fences, through windows, in bins/bin cupboard,
                              wheelie bins or too exposed to the elements.
                            </Text>
                          </Box>

                          <Button
                            colorVariant={ButtonColorVariant.Secondary}
                            width="100%"
                            disabled={shippingAddress.shippingDefault}
                          >
                            Set to default
                          </Button>

                          <Space size={2} direction="vertical" />
                          <Button
                            colorVariant={ButtonColorVariant.Primary}
                            width="100%"
                            type="submit"
                          >
                            Save
                          </Button>
                        </Form>
                      </Formik>
                    </Box>
                  )}
                </Join>
              </div>
            )
          })}
      </Accordion>
    </>
  )
}

export { DeliveryAddressSection }
